import type { TourStep } from '@/components/ui/Tour';
import { TERM_MANAGEMENT_TOUR_STEP_IDS, TOUR_CENTERED_STEP_SELECTOR } from './tour-constants';
import { TourStepContent } from './tours/StepContent';

const termListWithTermsTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Current and past terms"
        description="Switch between upcoming seasons and completed terms to find what you need."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.LIST_TABS,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Search and filter"
        description="Narrow the list by name or active status when you manage many seasons."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.FILTERS,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Create a term"
        description="Open the form to set dates and optionally copy lessons from a previous season."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.NEW_TERM,
    position: 'bottom',
  },
];

const termListEmptyTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Terms overview"
        description="Terms group your group lessons into seasons. Open the guide here any time for a refresher on how terms and lessons fit together."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.GUIDE,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Create a term"
        description="Start with New Term to set the season dates, then add group lessons inside the term."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.NEW_TERM,
    position: 'bottom',
  },
];

/** Terms list page — tabs, filters, and new term entry point. */
export function getTermListTourSteps(options?: { hasTerms?: boolean }): TourStep[] {
  return options?.hasTerms === false ? termListEmptyTourSteps : termListWithTermsTourSteps;
}

export const termListTourSteps = termListWithTermsTourSteps;

/** Shown while the create-term drawer is open (first-time or replay). */
export const termCreateTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Create a term"
        description="This form sets the season window, optional registration deadline, and whether you copy group lessons from a previous term."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.FORM_DRAWER,
    position: 'left',
  },
  {
    content: (
      <TourStepContent
        title="Name your term"
        description="Give the season a clear name and choose whether it is active for scheduling and public enrolment."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.FORM_DETAILS,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Set the season dates"
        description="Pick start and end dates for this term. Duration is calculated automatically so you can confirm the window at a glance."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.FORM_SCHEDULE,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Copy from a previous term"
        description="When you are rolling into a new season, copy group lessons from an earlier term to save setup time. You can edit them after saving."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.FORM_COPY,
    position: 'top',
  },
  {
    content: (
      <TourStepContent
        title="Create the term"
        description="Save when you are ready. You will land inside the term to add lessons and choose how you want to view the schedule."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.FORM_SUBMIT,
    position: 'top',
  },
];

/** Shown while the add-lesson-to-term drawer is open (first-time or replay). */
export const termLessonCreateTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Lesson basics"
        description="Choose group or private, name the lesson, pick a sport, and optionally link a development program to prefill details."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.LESSON_FORM_DETAILS,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Schedule & staff"
        description="Assign courts and coaches, then set which weekdays and times this lesson runs during the term."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.LESSON_FORM_SCHEDULE,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Pricing & enrolment"
        description="Set term price, optional per-session price, capacity, skill level, and age group for matching students."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.LESSON_FORM_PRICING,
    position: 'top',
  },
  {
    content: (
      <TourStepContent
        title="Create the lesson"
        description="Save when you are ready. The lesson appears in your term schedule and you can manage students from there."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.LESSON_FORM_SUBMIT,
    position: 'top',
  },
];

/** Shown inside a term after it is created (or when replaying from term detail). */
export const termManageTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Manage academic terms"
        description="This is where you organise your academy seasons. Open a term to build the schedule, add group lessons, and manage enrolments throughout the season."
      />
    ),
    selectorId: TOUR_CENTERED_STEP_SELECTOR,
  },
  {
    content: (
      <TourStepContent
        title="Your term overview"
        description="Review dates, status, and registration settings here. Use Edit term any time to adjust the season window."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.MANAGE_HEADER,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Add group lessons"
        description="Create recurring lessons for this term — set days, times, courts, coaches, capacity, and pricing."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.MANAGE_ADD_LESSON,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Switch views"
        description="Use list, weekly schedule, or day-by-court views. Export, import, and filter by location from the actions menu."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.MANAGE_VIEWS,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Lesson schedule"
        description="Your term lessons appear here. Open a lesson to edit details, manage the student roster, or reschedule a single session."
      />
    ),
    selectorId: TERM_MANAGEMENT_TOUR_STEP_IDS.MANAGE_LESSONS,
    position: 'top',
  },
];
